import { QueryClient, dehydrate } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import Ad from 'src/components/ads/Ad';
import StickyBottomAd from "src/components/ads/StickyBottomAd";
import ContentWrapper from 'src/components/contentwrapper/ContentWrapper';
import FilmwebNews from 'src/components/filmwebnews/FilmwebNews';
import AdUnitWithPosition from "src/components/forsiden/AdUnitWithPosition";
import KinotoppenLoader from 'src/components/kinotoppen/KinotoppenLoader';
import ShowMovieChart from 'src/components/kinotoppen/ShowMovieChart';
import PopularMoviesList, { POP_MOVIES_QUERY } from 'src/components/popularmovieslist/PopularMoviesList';
import KinotoppenSeo from 'src/components/seo/KinotoppenSeo';
import TopOfPageSponsor from "src/components/takoversponsor/TopOfPageSponsor";
import { buildFilminfoQueryKey, filminfoRequest } from 'src/lib/client/useFilminfo';
import AdWrapper from 'src/lib/contexts/AdContext';
import { FRONTPAGE_NEWS, filterOtherNews } from 'src/lib/groq';
import { MovieChart } from 'src/lib/movieinfo/movieinfotypes';
import { useOverriddenAdunits } from "src/lib/server/ads";
import { setDefaultCacheHeaders } from 'src/lib/server/cache';
import { ICommonProps, getServerData, sanityQuery } from 'src/lib/server/sanity';
import styled from 'src/lib/styles/css';
import IFilmnytt from 'src/lib/types/Filmnytt';

//#region [Other]getServerSideProps
export const getServerSideProps: GetServerSideProps = getServerData(async (context) => {
    setDefaultCacheHeaders(context);

    // preload popular movies
    const queryClient = new QueryClient();
    const prefetchPopMoviesPromise = queryClient.prefetchQuery({
        queryKey: buildFilminfoQueryKey(POP_MOVIES_QUERY),
        queryFn: filminfoRequest
    });

    // TODO: preload default view
    const otherNewsQueryPromise = sanityQuery<IFilmnytt[]>(FRONTPAGE_NEWS);
    const [otherNews] = await Promise.all([otherNewsQueryPromise, prefetchPopMoviesPromise]);

    return {
        props: {
            otherNews: filterOtherNews(otherNews)?.slice(0, 3) ?? null,
            dehydratedState: dehydrate(queryClient),
            adTargetIds: ADS.filter(a => !!a.targetId).map( a => a.targetId!)
        }
    };

});
//#endregion


//#region [Props]
type KinotoppenProps = {
    otherNews: IFilmnytt[] | null;
} & ICommonProps;
//#endregion

//#region [Component]
export default function Kinotoppen({ appData, otherNews, adUnits, hasSponsor, sponsorTopImageDesktop, sponsorTopImageMobile, sponsorClickUrl }: KinotoppenProps) {
    const finalAdUnits = useOverriddenAdunits(ADS, adUnits);
    const hasPackshot = finalAdUnits.find(adUnit => adUnit.targetId === "FW_bottomSticky")?.isPackshot ?? false;
    return <section>
        <KinotoppenSeo />
        <ContentWrapper>
            <AdWrapper ads={finalAdUnits} appData={appData}>
                <SAd targetId='FW_topp_kinotoppen' removeIfNoAd supportsFixedAd />
                <SHeaderWrapper>
                    {hasSponsor && <STopOfPageSponsor desktopImages={sponsorTopImageDesktop} mobileImages={sponsorTopImageMobile} clickUrl={sponsorClickUrl} />}
                    <SPageTitle>Kinotoppen</SPageTitle>
                </SHeaderWrapper>

                <KinotoppenLoader>
                    {(movieChart: MovieChart | null | undefined, showAllMovies: boolean) => <ShowMovieChart movieChart={movieChart} showAllMovies={showAllMovies}><SAd targetId='FW_midt1_kinotoppen' removeIfNoAd supportsFixedAd /></ShowMovieChart>}
                </KinotoppenLoader>

                <SAd targetId='FW_bunn_kinotoppen' removeIfNoAd supportsFixedAd />

                <SPopularMoviesList />

                <SFilmwebNews articles={otherNews} />
                <StickyBottomAd isPackshot={hasPackshot} />
            </AdWrapper>
        </ContentWrapper>
    </section>;
}
//#endregion

//#region [Styles]
const SPopularMoviesList = styled(PopularMoviesList)`
	margin-bottom: 50px;
	margin-left: 10px;
	@media (min-width: 550px) {
		margin-left: 0;
	}
`;

const SHeaderWrapper = styled.div`
    @media (min-width: 550px) {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
    }
`;

const STopOfPageSponsor = styled(TopOfPageSponsor)`
    @media (min-width: 550px) {
        grid-column: 3 / 3;
        grid-row: 1 / 1;

        .topOfPageSponsorInnerContainer {
            margin-bottom: 0;
        }
    }
`;

const SFilmwebNews = styled(FilmwebNews)`
	margin-bottom: 50px;
`;

const SPageTitle = styled.h1`
	font-size: 54px;
	text-align: center;
	margin-top: 30px;
	color: var(--textcolor);
    @media (min-width: 550px) {
        grid-column: 2 / 2;
        grid-row: 1 / 1;
    }
`;

const SAd = styled(Ad)`
	margin-top: 20px;
	margin-bottom: 20px;
`;
//#endregion

const ADS: AdUnitWithPosition[] = [
    { auId: '000000000005375a', auW: '640', auH: '150', targetId: 'FW_topp_kinotoppen' },
    { auId: '0000000000053786', auW: '640', auH: '150', targetId: 'FW_midt1_kinotoppen' },
    { auId: '0000000000053933', auW: '640', auH: '150', targetId: 'FW_bunn_kinotoppen' },
    { auId: '3c3cd5', auW: '50vw', targetId: 'FW_bottomSticky', requestMode: "hasTarget" },

];